/**
 * Bill Validation — a bill's status, from invoice to closed (client spec, Oct 2026).
 *
 *   Missing Invoice → Missing Data → Bill run → Report
 *     → matched:    Ready to Send → (sent) → Completed
 *                   (Needs Internal Review can be set here too: a verdict within
 *                   range can still hide missing supply or supplementary charges)
 *     → mismatched: Mismatched → (Needs Internal Review) → Follow up with Retailer
 *                   → Retailer Corrected Mismatch
 *                   → Ready to Send / Sent to Customer → Waiting on Customer Response → Completed
 *
 * Most of it is derived from data that already exists (lib/billPipeline for the
 * stages, the report verdict, the report send history). Only six steps are a
 * person's decision, stored on bv_bill.workflow_status (migrations 131, 142, 154):
 * internal review, system review, the two retailer steps, waiting on the customer, and closing
 * a mismatched bill. The
 * Bulk Bill Run board and the Bills list both read their status from here, so
 * they cannot disagree.
 */
import type { StageKey } from './billPipeline';
import type { ReportBucket, ReportSendStatus } from './reportStatus';

/** The steps a person sets — bv_bill.workflow_status. */
export type WorkflowStep =
  | 'internal_review' | 'system_review' | 'follow_up_retailer' | 'retailer_corrected' | 'waiting_customer' | 'completed';

export type BillWorkflowStatus =
  | 'missing_invoice' | 'missing_data' | 'bill_run' | 'report'
  | 'mismatched' | 'internal_review' | 'system_review' | 'follow_up_retailer' | 'retailer_corrected'
  | 'ready_to_send' | 'sent_to_customer' | 'waiting_customer' | 'completed';

export const WORKFLOW_ORDER: BillWorkflowStatus[] = [
  'missing_invoice', 'missing_data', 'bill_run', 'report', 'mismatched',
  'internal_review', 'system_review', 'follow_up_retailer', 'retailer_corrected', 'ready_to_send',
  'sent_to_customer', 'waiting_customer', 'completed',
];

export const WORKFLOW_LABELS: Record<BillWorkflowStatus, string> = {
  missing_invoice: 'Missing Invoice',
  missing_data: 'Missing Data',
  bill_run: 'Bill run',
  report: 'Report',
  mismatched: 'Mismatched',
  // A mismatch the team checks before raising it with the retailer: often a
  // gap in the system's own data (tariff, contract, mapping) rather than a
  // retailer error.
  internal_review: 'Needs Internal Review',
  // The system's own figures look wrong (calculation, parsing, missing config):
  // held for the build team rather than the analysts. Sends like Internal Review.
  system_review: 'Needs System Review',
  follow_up_retailer: 'Follow up with Retailer',
  retailer_corrected: 'Retailer Corrected Mismatch',
  ready_to_send: 'Ready to Send',
  sent_to_customer: 'Sent to Customer',
  waiting_customer: 'Waiting on Customer Response',
  completed: 'Completed',
};

export const STEP_LABELS: Record<WorkflowStep, string> = {
  internal_review: WORKFLOW_LABELS.internal_review,
  system_review: WORKFLOW_LABELS.system_review,
  follow_up_retailer: WORKFLOW_LABELS.follow_up_retailer,
  retailer_corrected: WORKFLOW_LABELS.retailer_corrected,
  waiting_customer: WORKFLOW_LABELS.waiting_customer,
  completed: WORKFLOW_LABELS.completed,
};

export type WorkflowInput = {
  /** billPipeline's first stage not passed, or 'done' once the report exists. */
  current: StageKey | 'done';
  /** The report's verdict; null before there is a report. */
  bucket: ReportBucket | null;
  sendStatus: ReportSendStatus;
  step: WorkflowStep | null;
};

const MISSING_INVOICE: StageKey[] = ['assigned', 'period', 'parsed', 'nmi'];
const MISSING_DATA: StageKey[] = ['usage', 'network', 'tou'];

export function billWorkflowStatus({ current, bucket, sendStatus, step }: WorkflowInput): BillWorkflowStatus {
  // Before the report exists the stages decide; a step set earlier waits.
  if (current !== 'done') {
    if (MISSING_INVOICE.includes(current)) return 'missing_invoice';
    if (MISSING_DATA.includes(current)) return 'missing_data';
    return current === 'calculated' ? 'bill_run' : 'report';
  }

  if (step === 'completed') return 'completed';
  const mismatched = bucket === 'overcharge' || bucket === 'undercharge';
  // A bill a person has started working is on the mismatch path even after its
  // corrected invoice comes back within range.
  const mismatchPath = mismatched || step !== null;

  if (sendStatus === 'sent') {
    if (step === 'waiting_customer') return 'waiting_customer';
    return mismatchPath ? 'sent_to_customer' : 'completed';
  }

  if (step === 'waiting_customer') return 'waiting_customer';
  // Checked before the verdict: a person can hold any bill for internal review,
  // within range or not (spec 2026-10-19 — missing supply charges).
  if (step === 'internal_review' || step === 'system_review') return step;
  if (!mismatched) return 'ready_to_send';
  if (step === 'follow_up_retailer') return 'follow_up_retailer';
  if (step === 'retailer_corrected') return 'retailer_corrected';
  return 'mismatched';
}

/** Holds a person can put on any bill, within range or not. */
const REVIEW_STEPS: WorkflowStep[] = ['internal_review', 'system_review'];
const MISMATCH_STEPS: WorkflowStep[] = [...REVIEW_STEPS, 'follow_up_retailer', 'retailer_corrected'];

/**
 * The steps a person may set from this status; empty where the system decides.
 * `bucket` is the report's verdict: a within-range bill in internal review is
 * offered internal review only (and its clear) — the retailer steps are for a
 * mismatch, and on a within-range bill they would read Ready to Send at once.
 */
export function stepsFor(status: BillWorkflowStatus, bucket: ReportBucket | null = null): WorkflowStep[] {
  switch (status) {
    case 'ready_to_send':
      return [...REVIEW_STEPS];
    case 'internal_review':
    case 'system_review':
      return bucket === 'within_range' ? [...REVIEW_STEPS] : [...MISMATCH_STEPS];
    case 'mismatched':
    case 'follow_up_retailer':
    case 'retailer_corrected':
      return [...MISMATCH_STEPS];
    case 'sent_to_customer':
    case 'waiting_customer':
      return ['waiting_customer', 'completed'];
    default:
      return [];
  }
}
